import { useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import * as THREE from 'three';
import {
  BRICK_CLAY_DUST,
  BRICK_MORTAR_DUST,
} from '../../materials/appearanceDescriptor';
import type { ProfileId } from '../../services/destructionProfiles';
import { demolitionResidueTint } from '../particles/demolitionDust';
import {
  clearPowderResidue,
  createPowderResidueField,
  powderSplatAmount,
  powderSplatRadius,
  POWDER_TEX_SIZE,
  POWDER_WORLD,
  stampPowderSplat,
  worldToPowderUv,
  type PowderResidueField,
} from './powderResidue';

export interface PowderResidueStamp {
  x: number;
  z: number;
  volume: number;
  material: ProfileId;
  /** Extra debris-rest offsets in world XZ (already computed). */
  rest?: ReadonlyArray<{ x: number; z: number }>;
}

export interface PowderResidueController {
  stamp: (input: PowderResidueStamp) => void;
  clear: () => void;
}

function parseHex(hex: string): { r: number; g: number; b: number } {
  const raw = hex.startsWith('#') ? hex.slice(1) : hex;
  const n = Number.parseInt(raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function uploadField(texture: THREE.CanvasTexture, field: PowderResidueField) {
  const canvas = texture.image as HTMLCanvasElement;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const image = ctx.createImageData(field.size, field.size);
  image.data.set(field.data);
  ctx.putImageData(image, 0, 0);
  texture.needsUpdate = true;
  field.dirty = false;
}

export function PowderResidueLayer({
  material,
  wallHalfZ,
  wallWidth,
  residueGeneration,
  controllerRef,
}: {
  material: ProfileId;
  wallHalfZ: number;
  wallWidth: number;
  residueGeneration: number;
  controllerRef: MutableRefObject<PowderResidueController | null>;
}) {
  const fieldRef = useRef<PowderResidueField>(createPowderResidueField());
  const generationRef = useRef(residueGeneration);
  const canvas = useMemo(() => {
    const el = document.createElement('canvas');
    el.width = POWDER_TEX_SIZE;
    el.height = POWDER_TEX_SIZE;
    return el;
  }, []);
  const texture = useMemo(() => {
    const tex = new THREE.CanvasTexture(canvas);
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.generateMipmaps = true;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.needsUpdate = true;
    return tex;
  }, [canvas]);

  useEffect(() => () => {
    texture.dispose();
  }, [texture]);

  useEffect(() => {
    if (residueGeneration === generationRef.current) return;
    generationRef.current = residueGeneration;
    clearPowderResidue(fieldRef.current);
    uploadField(texture, fieldRef.current);
  }, [residueGeneration, texture]);

  useEffect(() => {
    const twoTone = material === 'brick';
    const pile = demolitionResidueTint(material);
    const pileRgb = {
      r: Math.round(pile[0] * 255),
      g: Math.round(pile[1] * 255),
      b: Math.round(pile[2] * 255),
    };
    const clay = twoTone ? parseHex(BRICK_CLAY_DUST) : pileRgb;
    const mortar = twoTone ? parseHex(BRICK_MORTAR_DUST) : pileRgb;
    const amountScale = material === 'glass' ? 0.12 : 1;

    controllerRef.current = {
      stamp(input) {
        if (!(input.volume > 1e-8)) return;
        const radius = powderSplatRadius(input.volume);
        const amount = powderSplatAmount(input.volume) * amountScale;
        stampPowderSplat(fieldRef.current, {
          x: input.x,
          z: input.z,
          radius,
          color: clay,
          amount,
        });
        if (twoTone) {
          stampPowderSplat(fieldRef.current, {
            x: input.x + 0.18,
            z: input.z + 0.22,
            radius: radius * 0.72,
            color: mortar,
            amount: amount * 0.7,
          });
        }
        const rest = input.rest ?? [];
        const restN = Math.min(rest.length, 6);
        for (let i = 0; i < restN; i += 1) {
          stampPowderSplat(fieldRef.current, {
            x: rest[i].x,
            z: rest[i].z,
            radius: radius * 0.45,
            color: i % 2 === 0 ? clay : mortar,
            amount: amount * 0.35,
          });
        }
        // Forward apron — debris typically lands in +Z of the wall face.
        stampPowderSplat(fieldRef.current, {
          x: input.x,
          z: input.z + Math.min(1.8, 0.55 + radius * 0.45),
          radius: radius * 0.95,
          color: twoTone ? mortar : clay,
          amount: amount * 0.7,
        });
        stampPowderSplat(fieldRef.current, {
          x: input.x,
          z: input.z + Math.min(2.6, 1.15 + radius * 0.55),
          radius: radius * 1.05,
          color: twoTone ? clay : mortar,
          amount: amount * 0.38,
        });
        uploadField(texture, fieldRef.current);
      },
      clear() {
        clearPowderResidue(fieldRef.current);
        uploadField(texture, fieldRef.current);
      },
    };
    return () => {
      controllerRef.current = null;
    };
  }, [controllerRef, material, texture]);

  const overlayW = POWDER_WORLD.width;
  const overlayD = POWDER_WORLD.depth;
  const overlayX = POWDER_WORLD.x0 + overlayW / 2;
  const overlayZ = POWDER_WORLD.z0 + overlayD / 2;
  const wallBaseGeo = useMemo(() => {
    const geo = new THREE.PlaneGeometry(wallWidth + 0.25, 0.34);
    const uv = geo.getAttribute('uv');
    const left = worldToPowderUv(-wallWidth / 2, wallHalfZ);
    const right = worldToPowderUv(wallWidth / 2, wallHalfZ);
    // Vertical fade: sample a thin band around the wall foot so the strip
    // actually shows the nearby residue, not the whole atlas.
    const v0 = left.v - 0.02;
    const v1 = left.v + 0.02;
    uv.setXY(0, left.u, v0);
    uv.setXY(1, right.u, v0);
    uv.setXY(2, left.u, v1);
    uv.setXY(3, right.u, v1);
    uv.needsUpdate = true;
    return geo;
  }, [wallHalfZ, wallWidth]);
  useEffect(() => () => {
    wallBaseGeo.dispose();
  }, [wallBaseGeo]);

  return (
    <group>
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[overlayX, 0.032, overlayZ]}
        receiveShadow
      >
        <planeGeometry args={[overlayW, overlayD]} />
        <meshStandardMaterial
          map={texture}
          color="#ffffff"
          transparent
          depthWrite={false}
          roughness={0.96}
          metalness={0}
          envMapIntensity={0.35}
          polygonOffset
          polygonOffsetFactor={-2}
          polygonOffsetUnits={-2}
        />
      </mesh>
      <mesh
        geometry={wallBaseGeo}
        position={[0, 0.16, wallHalfZ + 0.018]}
        renderOrder={2}
      >
        <meshStandardMaterial
          map={texture}
          color="#ffffff"
          transparent
          depthWrite={false}
          roughness={0.96}
          metalness={0}
          opacity={0.92}
          envMapIntensity={0.3}
          polygonOffset
          polygonOffsetFactor={-1}
          polygonOffsetUnits={-1}
        />
      </mesh>
    </group>
  );
}
